/**
 * Recipe quantity scaling + unit-system conversion for the chef surfaces
 * (deliverable detail panel, recipes drawer, print pack).
 *
 * Authored recipe amounts carry a `quantityBasis`:
 *   - RECIPE_YIELD (default) — the amount makes the WHOLE batch
 *     (`baseYield` servings), so per-serving = amount / baseYield.
 *   - PER_SERVING / COMPONENT_SERVING — the amount is for one serving.
 *
 * The detail panel's multiplier ("200x") means "prep N servings", so the
 * displayed amount is per-serving × N, then converted into the selected
 * unit system (imperial lbs/oz vs metric kg/g). Count-style units (each,
 * slice, packet…) scale their quantity but keep their unit.
 *
 * Pure + relative-import-free so bun test can import it directly.
 */

import { cupsFromWeight, formatMeasureText, formatMeasureValue } from '@ordo/constants/measure-format'
import { sameDimensionFactor, volumeToCups, weightToGrams } from '@ordo/constants/measures'

export type UnitSystem = 'imperial' | 'metric'

/** "2 cups + 2 tbsp" (shorthand) vs "1.06 lb" (measurement). */
export type AmountStyle = 'shorthand' | 'measurement'

/**
 * The kitchen-wide reading mode (Settings > My Kitchen > Measurement display).
 *   - standard  — weight → volume by density, `system` ladder, cook shorthand.
 *   - technical — exactly as the menus team entered it: authored unit, no
 *                 conversion, plain decimals. `system` is ignored.
 */
export type MeasurementDisplay = 'standard' | 'technical'

export interface ScaleContext {
  /** Serving count the chef is prepping (the "200x" multiplier). */
  servings: number
  /** Servings one authored batch yields (`Recipe.baseYield`, min 1). */
  baseYield: number
  /**
   * The shipped serving over the recipe's PRIMARY serving (`Component.
   * servingMultiplier`). Amounts are authored for the primary, so a
   * non-primary portion must scale by this. Absent = 1 (ships at primary).
   */
  servingMultiplier?: number
  system: UnitSystem
  /** Cook-style shorthand vs plain measurement. Default shorthand. */
  style?: AmountStyle
  /** Standard (convert) vs Technical (as authored). Default standard. */
  mode?: MeasurementDisplay
}

/** Milliliters per US cup — bridges the canonical cup pivot to the ml ladders. */
const ML_PER_CUP = 236.588

/**
 * Per-serving amount from an authored quantity + its basis, or NULL when it
 * cannot be honestly derived. Under the default RECIPE_YIELD basis a missing
 * or non-positive `baseYield` used to be treated as yield-of-1 — the whole
 * BATCH amount became the "per-serving" amount and a 200× multiplier printed
 * two hundred batches' worth of an ingredient. Unknown means unknown: the
 * caller renders the name without an amount (the same rule `displayAmount`
 * already applies to a missing quantity/unit).
 */
export function perServingQuantity(
  quantity: number,
  quantityBasis: string | null,
  baseYield: number | null,
): number | null {
  if (quantityBasis === 'PER_SERVING' || quantityBasis === 'COMPONENT_SERVING') {
    return quantity
  }
  // RECIPE_YIELD (and unknown/legacy null) — amounts make the whole batch.
  if (baseYield === null || !Number.isFinite(baseYield) || baseYield <= 0) {
    return null
  }
  return quantity / baseYield
}

export interface Measure {
  quantity: number
  unit: string
}

function fromLadder(amount: number, ladder: ReadonlyArray<readonly [string, number]>): Measure {
  for (const [unit, per] of ladder) {
    if (amount >= per) {
      return { quantity: amount / per, unit }
    }
  }
  const last = ladder[ladder.length - 1]!
  return { quantity: amount / last[1], unit: last[0] }
}

const IMPERIAL_WEIGHT: ReadonlyArray<readonly [string, number]> = [
  ['lb', 453.592],
  ['oz', 28.3495],
]
const METRIC_WEIGHT: ReadonlyArray<readonly [string, number]> = [
  ['kg', 1000],
  ['g', 1],
]
const IMPERIAL_VOLUME: ReadonlyArray<readonly [string, number]> = [
  ['gal', 3785.41],
  ['qt', 946.353],
  ['cups', 236.588],
  ['tbsp', 14.7868],
  ['tsp', 4.92892],
]
const METRIC_VOLUME: ReadonlyArray<readonly [string, number]> = [
  ['L', 1000],
  ['ml', 1],
]

/**
 * Convert a measure into the selected unit system, picking the largest
 * sensible unit (7.5 lb, not 120 oz). Unrecognized units (each, slice,
 * packet, …) pass through unchanged — they're counts, not measures.
 *
 * Unit recognition delegates to `@ordo/constants/measures` — the ONE
 * vocabulary. This file used to carry its own divergent copy (no `mg`, no
 * `floz`/`c`/`tbs`, weaker normalization), so the recipe page and the
 * shopping list of one printed packet could report different numbers for
 * the same ingredient — the exact failure the canonical module was built
 * to close.
 */
export function convertMeasure(measure: Measure, system: UnitSystem): Measure {
  const grams = weightToGrams(measure.quantity, measure.unit)
  if (grams !== null) {
    return fromLadder(grams, system === 'imperial' ? IMPERIAL_WEIGHT : METRIC_WEIGHT)
  }
  const cups = volumeToCups(measure.quantity, measure.unit)
  if (cups !== null) {
    return fromLadder(cups * ML_PER_CUP, system === 'imperial' ? IMPERIAL_VOLUME : METRIC_VOLUME)
  }
  return measure
}

/** @deprecated Prefer `formatMeasureValue` from `@ordo/constants`. */
export function formatQuantityValue(quantity: number): string {
  return formatMeasureValue(quantity)
}

export interface DisplayableAmount {
  quantity: number | null
  unit: string | null
  quantityBasis: string | null
  /** Grams per US cup for this ingredient; enables weight → volume. */
  gramsPerCup?: number | null
}

/**
 * Scaled + converted display string for one recipe amount, or null when the
 * amount is unresolvable (the caller renders the ingredient name only —
 * never a fabricated "0 unit").
 */
export function displayAmount(amount: DisplayableAmount, ctx: ScaleContext): string | null {
  const scaled = scaledAmount(amount, ctx)
  if (scaled === null || amount.unit === null) {
    return null
  }
  // Technical: the scaled amount in the authored unit, plain decimals. No
  // density, no ladder — what the menus team typed is what the cook reads.
  if (ctx.mode === 'technical') {
    return formatMeasureText(scaled, amount.unit, { system: 'as-authored', use: 'PURCHASING' })
  }
  const use = ctx.style === 'measurement' ? 'PURCHASING' : 'COOKING'
  // Weight → volume, where a trustworthy per-ingredient density exists.
  // Recipes are AUTHORED in metric grams for precision, but a cook measures
  // pourables and powders by volume — density (USDA CNDB or a hand-set
  // override, delivered on the wire) is what bridges the two. No density, or
  // an amount below a quarter teaspoon, and the authored weight stands.
  const cups = cupsFromWeight(scaled, amount.unit, amount.gramsPerCup)
  if (cups !== null) {
    return formatMeasureText(cups, 'cup', { system: ctx.system, use })
  }
  return formatMeasureText(scaled, amount.unit, { system: ctx.system, use })
}

/**
 * The scaled amount in the AUTHORED unit, with no unit-system conversion, or
 * null when the amount is unresolvable.
 *
 * `displayAmount` formats this. A scaling cap is compared against it, because
 * a cap is authored in kitchen units and has to be judged BEFORE the
 * imperial/metric ladder rewrites the unit out from under it.
 */
export function scaledAmount(amount: DisplayableAmount, ctx: ScaleContext): number | null {
  if (amount.quantity === null || amount.unit === null) {
    return null
  }
  const perServing = perServingQuantity(amount.quantity, amount.quantityBasis, ctx.baseYield)
  if (perServing === null) {
    return null
  }
  // Amounts are authored for the recipe's PRIMARY serving; a component shipped
  // at a non-primary portion scales by that ratio (chef report 2026-08-31 —
  // 1/4-cup CACFP PK vegetables against 1-Cup primaries ordered 4x the food).
  // Applied HERE, the single scaling point, so the cap advisory judges the
  // portion the kitchen actually plates rather than a full primary serving.
  const scaled = perServing * (ctx.servingMultiplier ?? 1) * ctx.servings
  return Number.isFinite(scaled) ? scaled : null
}

/** The three authored non-linear-scaling-cap columns off a `RecipeIngredient`. */
export interface ScalingCapFields {
  maxQuantityValue?: number | null
  maxQuantityUnit?: string | null
  scalingNotes?: string | null
}

/** A cap the displayed amount has run past — ADVISORY data, never a clamp. */
export interface CapAdvisory {
  /** The authored cap in its own unit, e.g. "8 oz". */
  capLabel: string
  /** The cap restated in the line's own unit, for callers that want to print
   *  "cap ≈ 227 g" beside a gram amount. */
  capInLineUnit: number
  /** How many times over the cap the displayed amount runs (always > 1). */
  overBy: number
  /** The author's `scalingNotes` prose, when one was written. */
  notes: string | null
}

/**
 * Decide whether an already-scaled amount has run past the line's authored
 * non-linear scaling cap.
 *
 * WHY THIS ADVISES AND DOES NOT CLAMP. The caps in production read "cap at
 * 8 oz per batch / roasting pan", and nothing in the system knows how many
 * pans a run uses. Clamping a 300-serving run's total to 8 oz would
 * under-dose by ~10x — a wider and quieter bug than showing the linear
 * number. The authored note says so itself: "unless an approved kitchen ratio
 * overrides it". It is guidance for the chef, not an absolute the software may
 * enforce. So this returns advisory data for the UI to show ALONGSIDE the
 * computed amount, and never alters the amount.
 *
 * Returns null (no advisory) when:
 *   - no cap is authored, or the cap value is non-finite / non-positive
 *   - the cap carries no unit
 *   - the scaled amount is unresolvable
 *   - the two units are not in the same measurement dimension, so no honest
 *     comparison exists (g vs each, `fl oz` vs `oz`). COUNT units compare
 *     only against the identical label — six slices is not six eaches.
 *   - the scaled amount is at or under the cap
 *
 * Pure — exported for unit tests.
 */
export function capAdvisory(
  scaled: number | null,
  unit: string | null,
  maxValue: number | null | undefined,
  maxUnit: string | null | undefined,
  notes?: string | null,
): CapAdvisory | null {
  if (maxValue === null || maxValue === undefined || !Number.isFinite(maxValue) || maxValue <= 0) {
    return null
  }
  const capUnit = maxUnit?.trim()
  if (!capUnit) {
    return null
  }
  if (scaled === null || !Number.isFinite(scaled) || unit === null || !unit.trim()) {
    return null
  }
  // Restate the line amount in the cap's unit. Null means different dimensions
  // (or two distinct COUNT labels) — there is no honest comparison to make.
  const toCapUnit = sameDimensionFactor(unit, capUnit)
  if (toCapUnit === null) {
    return null
  }
  const scaledInCapUnit = scaled * toCapUnit
  if (scaledInCapUnit <= maxValue) {
    return null
  }
  return {
    capLabel: `${formatQuantityValue(maxValue)} ${capUnit}`,
    capInLineUnit: maxValue / toCapUnit,
    overBy: scaledInCapUnit / maxValue,
    notes: notes?.trim() ? notes.trim() : null,
  }
}

/** {@link capAdvisory} against the amount {@link displayAmount} would render. */
export function capAdvisoryForAmount(
  amount: DisplayableAmount & ScalingCapFields,
  ctx: ScaleContext,
): CapAdvisory | null {
  return capAdvisory(
    scaledAmount(amount, ctx),
    amount.unit,
    amount.maxQuantityValue,
    amount.maxQuantityUnit,
    amount.scalingNotes,
  )
}

/** Multiplier choices for the detail panel: 1x plus round steps, always
 *  including (and defaulting to) the deliverable's own order count. */
export function multiplierOptions(orderCount: number): number[] {
  const set = new Set([1, 10, 25, 50, 100])
  if (orderCount >= 1) {
    set.add(orderCount)
  }
  return [...set].sort((a, b) => a - b)
}
